import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  PlayIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  MagnifyingGlassPlusIcon,
  XMarkIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import {
  translationApi,
  voiceApi,
  audioUtilsApi,
  TranslationJob,
  Language,
} from '../../services/audioApi';
import { AudioPageLayout, HistoryPanel, TransactionDrawer, HistoryItem } from '@/components/Audio';
import AudioUploadZone from '@/components/Audio/AudioUploadZone';
import AudioTranslationProgress from '@/components/Audio/AudioTranslationProgress';
import AudioStepDetailModal from '@/components/Audio/AudioStepDetailModal';

type AudioSTTProvider = 'sarvam' | 'ai4bharat';
type AudioTranslationProvider = 'sarvam' | 'google' | 'deepl';
type AudioTTSProvider = 'elevenlabs' | 'sarvam' | 'ai4bharat';

const AudioTranslation: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sourceLanguage, setSourceLanguage] = useState<string>('auto');
  const [targetLanguage, setTargetLanguage] = useState<string>('hi');
  const [sttProvider, setSttProvider] = useState<AudioSTTProvider>('sarvam');
  const [translationProvider, setTranslationProvider] = useState<AudioTranslationProvider>('sarvam');
  const [ttsProvider, setTtsProvider] = useState<AudioTTSProvider>('elevenlabs');
  const [selectedVoice, setSelectedVoice] = useState<string>('');
  const [outputFormat, setOutputFormat] = useState<'mp3' | 'wav'>('mp3');
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);

  // History and drawer state
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<HistoryItem | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [jobDetails, setJobDetails] = useState<TranslationJob | null>(null);
  const [jobDetailsLoading, setJobDetailsLoading] = useState(false);
  const [expandedDrawerStep, setExpandedDrawerStep] = useState<string | null>(null);

  const { data: languagesData } = useQuery(
    ['audio-languages'],
    async () => (await audioUtilsApi.getSupportedLanguages()).data,
    { staleTime: 60 * 60 * 1000 }
  );
  const languages: Language[] = languagesData?.languages || [];

  const { data: voicesData, isLoading: voicesLoading } = useQuery(
    ['voices'],
    async () => (await voiceApi.getVoices()).data
  );
  const voicesArray = Array.isArray(voicesData) ? voicesData : voicesData?.voices || voicesData?.data || [];
  const activeVoices = voicesArray.filter((voice: any) => voice.isActive && voice.status === 'ready');

  const { data: jobsData } = useQuery(
    ['audio-jobs'],
    () => translationApi.getTranslationJobs({ limit: 50 }),
    {
      refetchInterval: (data: any) => {
        const hasActive = data?.data?.jobs?.some(
          (j: any) => j.status === 'processing' || j.status === 'pending'
        );
        return hasActive ? 3000 : 30000;
      },
    }
  );

  const startTranslationMutation = useMutation({
    mutationFn: (formData: FormData) =>
      translationApi.startTranslation(formData, (pct) => setUploadProgress(pct)),
    onSuccess: (data: any) => {
      toast.success('Audio translation started successfully!');
      setCurrentJobId(data.data.jobId);
      setSelectedFile(null);
      setUploadProgress(null);
      queryClient.invalidateQueries({ queryKey: ['audio-jobs'] });
    },
    onError: (error: any) => {
      setUploadProgress(null);
      toast.error(error.response?.data?.message || 'Failed to start translation');
    },
  });

  const isSubmitting = startTranslationMutation.isLoading;

  const getLanguageName = (code?: string) => {
    if (!code) return undefined;
    if (code === 'auto') return 'Auto-detect';
    const language = languages.find((lang) => lang.code === code);
    return language ? `${language.name} (${language.nativeName})` : code.toUpperCase();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) { toast.error('Please select an audio file'); return; }
    if (!targetLanguage) { toast.error('Please select a target language'); return; }
    if (!selectedVoice) { toast.error('Please select a voice'); return; }

    const formData = new FormData();
    formData.append('audio', selectedFile);
    formData.append('targetLanguage', targetLanguage);
    formData.append('voiceId', selectedVoice);
    formData.append('sttProvider', sttProvider);
    formData.append('translationProvider', translationProvider);
    formData.append('ttsProvider', ttsProvider);
    formData.append('outputFormat', outputFormat);
    if (sourceLanguage !== 'auto') {
      formData.append('sourceLanguage', sourceLanguage);
    }

    setUploadProgress(0);
    startTranslationMutation.mutate(formData);
  };

  const handleResetForm = () => {
    setSelectedFile(null);
    setSourceLanguage('auto');
    setTargetLanguage('hi');
    setSttProvider('sarvam');
    setTranslationProvider('sarvam');
    setTtsProvider('elevenlabs');
    setSelectedVoice('');
    setOutputFormat('mp3');
  };

  const handleCancelJob = async (jobId: string) => {
    try {
      await translationApi.cancelTranslation(jobId);
      toast.success('Job cancelled');
      queryClient.invalidateQueries({ queryKey: ['audio-jobs'] });
      if (currentJobId === jobId) setCurrentJobId(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to cancel job');
    }
  };

  const handleRetryJob = async (jobId: string) => {
    try {
      await translationApi.retryJob(jobId);
      toast.success('Retrying job from last completed step');
      queryClient.invalidateQueries({ queryKey: ['audio-jobs'] });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to retry job');
    }
  };

  const refreshJobDetails = async (jobId: string) => {
    try {
      const res = await translationApi.getJobDetails(jobId);
      const job = res?.data;
      if (job?.jobId) setJobDetails(job);
    } catch { /* silent */ }
  };

  // Map jobs to HistoryItem format whenever jobs data changes
  useEffect(() => {
    const jobs: TranslationJob[] = jobsData?.data?.jobs || [];
    const items: HistoryItem[] = jobs.map((job) => ({
      id: job.jobId,
      title: job.request?.targetLanguage
        ? `→ ${getLanguageName(job.request.targetLanguage) || job.request.targetLanguage}`
        : `Translation ${job.jobId.slice(0, 8)}`,
      createdAt: job.createdAt,
      status: job.status,
      type: 'audio-translation',
      metadata: {
        sourceLanguage: getLanguageName(job.request?.sourceLanguage) || 'Auto-detect',
        targetLanguage: getLanguageName(job.request?.targetLanguage),
        voice: job.result?.voiceName,
        sttProvider: job.request?.sttProvider,
        translationProvider: job.request?.translationProvider,
        ttsProvider: job.request?.ttsProvider,
        processingTime: job.result?.processingTime
          ? `${(job.result.processingTime / 1000).toFixed(1)}s`
          : undefined,
        progress: `${job.progress}%`,
        outputAudioUrl: job.result?.outputAudioUrl ?? null,
      },
    }));
    setHistoryItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsData, languages]);

  const handleHistoryItemClick = (item: HistoryItem) => {
    setSelectedHistoryItem(item);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setTimeout(() => { setSelectedHistoryItem(null); setJobDetails(null); }, 300);
  };

  // Fetch full job details (with steps) when drawer opens
  useEffect(() => {
    if (!isDrawerOpen || !selectedHistoryItem) { setJobDetails(null); return; }
    const jobs: TranslationJob[] = jobsData?.data?.jobs || [];
    const cached = jobs.find((j) => j.jobId === selectedHistoryItem.id);
    if (cached) setJobDetails(cached);
    setJobDetailsLoading(true);
    translationApi.getJobDetails(selectedHistoryItem.id)
      .then((res: any) => {
        const job = res?.data;
        if (job?.jobId) setJobDetails(job);
      })
      .catch(console.error)
      .finally(() => setJobDetailsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, selectedHistoryItem?.id]);

  // Keep polling job details while the drawer is open and it's actively processing
  useEffect(() => {
    if (!isDrawerOpen || !selectedHistoryItem) return;
    if (jobDetails?.status !== 'processing' && jobDetails?.status !== 'pending') return;
    const timer = setTimeout(() => {
      refreshJobDetails(selectedHistoryItem.id);
    }, 2000);
    return () => clearTimeout(timer);
  }, [isDrawerOpen, selectedHistoryItem?.id, jobDetails]);

  return (
    <>
      <AudioPageLayout
        title="Audio Translation"
        formSection={
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Audio Source</h2>
              <AudioUploadZone
                onFileSelect={setSelectedFile}
                selectedFile={selectedFile}
                isUploading={isSubmitting}
                uploadProgress={uploadProgress ?? undefined}
              />
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Translation Settings</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Source Language
                    </label>
                    <div className="relative">
                      <select
                        value={sourceLanguage}
                        onChange={(e) => setSourceLanguage(e.target.value)}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white bg-white text-gray-900 appearance-none cursor-pointer"
                      >
                        <option value="auto">Auto-detect</option>
                        {languages.map((lang) => (
                          <option key={lang.code} value={lang.code}>
                            {lang.name} ({lang.nativeName})
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Target Language *
                    </label>
                    <div className="relative">
                      <select
                        value={targetLanguage}
                        onChange={(e) => setTargetLanguage(e.target.value)}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white bg-white text-gray-900 appearance-none cursor-pointer"
                      >
                        {languages.map((lang) => (
                          <option key={lang.code} value={lang.code}>
                            {lang.name} ({lang.nativeName})
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* STT Provider */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Speech-to-Text Engine</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setSttProvider('sarvam')}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                        sttProvider === 'sarvam'
                          ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-base">🇮🇳</span> Sarvam AI
                      {sttProvider === 'sarvam' && <CheckCircleIcon className="h-4 w-4 ml-auto" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSttProvider('ai4bharat')}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                        sttProvider === 'ai4bharat'
                          ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-base">🔬</span> AI4Bharat
                      {sttProvider === 'ai4bharat' && <CheckCircleIcon className="h-4 w-4 ml-auto" />}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {sttProvider === 'ai4bharat'
                      ? 'IndicConformer — optimised for Indian languages, requires GPU container'
                      : 'Sarvam — cloud API, supports 10+ Indian languages and English'}
                  </p>
                </div>

                {/* Translation Provider */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Translation Engine</label>
                  <div className="grid grid-cols-3 gap-2">
                    {([
                      { id: 'sarvam', label: 'Sarvam AI', icon: '🇮🇳', desc: 'Optimised for Indian languages' },
                      { id: 'google', label: 'Google', icon: '🔵', desc: 'Supports 100+ languages' },
                      { id: 'deepl', label: 'DeepL', icon: '🌐', desc: 'High-quality neural translation' },
                    ] as const).map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setTranslationProvider(p.id)}
                        className={`flex flex-col items-start gap-0.5 px-3 py-2 rounded-lg border-2 text-xs font-medium transition-all ${
                          translationProvider === p.id
                            ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                            : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                        }`}
                      >
                        <span className="flex items-center gap-1.5 font-semibold">
                          <span>{p.icon}</span> {p.label}
                          {translationProvider === p.id && <CheckCircleIcon className="h-3.5 w-3.5 ml-auto" />}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400 font-normal">{p.desc}</span>
                      </button>
                    ))}
                  </div>
                  {translationProvider === 'google' && (
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">Requires GOOGLE_TRANSLATE_API_KEY in environment</p>
                  )}
                  {translationProvider === 'deepl' && (
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">Requires DEEPL_API_KEY in environment (free tier available)</p>
                  )}
                </div>

                {/* TTS Provider */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Text-to-Speech Engine</label>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => setTtsProvider('elevenlabs')}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                        ttsProvider === 'elevenlabs'
                          ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-base">🗣️</span> ElevenLabs
                      {ttsProvider === 'elevenlabs' && <CheckCircleIcon className="h-4 w-4 ml-auto" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => setTtsProvider('sarvam')}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                        ttsProvider === 'sarvam'
                          ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-base">🇮🇳</span> Sarvam
                      {ttsProvider === 'sarvam' && <CheckCircleIcon className="h-4 w-4 ml-auto" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => setTtsProvider('ai4bharat')}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                        ttsProvider === 'ai4bharat'
                          ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-base">🔬</span> AI4Bharat
                      {ttsProvider === 'ai4bharat' && <CheckCircleIcon className="h-4 w-4 ml-auto" />}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {ttsProvider === 'sarvam'
                      ? 'Sarvam TTS — supports pace adjustment for Indian languages'
                      : ttsProvider === 'ai4bharat'
                      ? 'IndicF5 — voice-cloned TTS, requires reference audio'
                      : 'ElevenLabs — default multilingual TTS with speed control'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Voice (TTS) *</label>
                  <div className="relative">
                    <select
                      value={selectedVoice}
                      onChange={(e) => {
                        const voice = activeVoices.find((v: any) => (v.id || v._id) === e.target.value);
                        setSelectedVoice(e.target.value);
                        if (voice?.provider && ['elevenlabs', 'sarvam', 'ai4bharat'].includes(voice.provider)) {
                          setTtsProvider(voice.provider);
                        }
                      }}
                      disabled={voicesLoading}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white bg-white text-gray-900 appearance-none cursor-pointer disabled:opacity-50"
                    >
                      <option value="" disabled>{voicesLoading ? 'Loading voices...' : 'Select Voice'}</option>
                      {activeVoices.map((voice: any) => (
                        <option key={voice.id || voice._id} value={voice.id || voice._id}>
                          {voice.name} {voice.gender && `(${voice.gender})`} {voice.language && `- ${voice.language}`}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  {activeVoices.length === 0 && !voicesLoading && (
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      No voices available. Create a voice clone first.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Output Format</label>
                  <select
                    value={outputFormat}
                    onChange={(e) => setOutputFormat(e.target.value as 'mp3' | 'wav')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="mp3">MP3</option>
                    <option value="wav">WAV</option>
                  </select>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleResetForm}
                    disabled={isSubmitting}
                    className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!targetLanguage || !selectedVoice || !selectedFile || isSubmitting}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        <span>
                          {uploadProgress !== null && uploadProgress < 100
                            ? `Uploading... ${uploadProgress}%`
                            : 'Starting Translation...'}
                        </span>
                      </>
                    ) : (
                      <>
                        <PlayIcon className="h-4 w-4" />
                        <span>Start Audio Translation</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Active Job Progress Popup */}
            {currentJobId && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                  <AudioTranslationProgress
                    jobId={currentJobId}
                    onCancel={() => handleCancelJob(currentJobId)}
                    onComplete={() => setCurrentJobId(null)}
                    onClose={() => setCurrentJobId(null)}
                  />
                </div>
              </div>
            )}
          </div>
        }
        historySection={
          <HistoryPanel
            items={historyItems}
            onItemClick={handleHistoryItemClick}
            selectedItemId={selectedHistoryItem?.id}
            renderItemActions={(item) => {
              if (item.status !== 'completed' || !item.metadata?.outputAudioUrl) return null;
              return (
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() =>
                      translationApi.downloadFile(
                        item.metadata!.outputAudioUrl,
                        `translated-audio-${item.id}.mp3`
                      )
                    }
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                  >
                    <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                    Download Translated Audio
                  </button>
                </div>
              );
            }}
          />
        }
      />

      <TransactionDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        item={selectedHistoryItem}
        size="vw70"
        hideMetadata
        title={selectedHistoryItem?.title}
        renderDetails={() => {
          const job = jobDetails;

          const stepStatusStyle = (status: string) => {
            switch (status) {
              case 'completed': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
              case 'processing': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
              case 'failed': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
              case 'skipped': return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
              default: return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
            }
          };

          const stepStatusIcon = (status: string) => {
            switch (status) {
              case 'completed': return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
              case 'processing': return <ArrowPathIcon className="h-4 w-4 text-blue-500 animate-spin" />;
              case 'failed': return <ExclamationCircleIcon className="h-4 w-4 text-red-500" />;
              default: return <ClockIcon className="h-4 w-4 text-yellow-500" />;
            }
          };

          const formatDuration = (ms?: number) => {
            if (!ms) return null;
            if (ms < 1000) return `${ms}ms`;
            if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
            return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
          };

          const detailRow = (label: string, value?: string | boolean | null) => {
            if (value === undefined || value === null || value === '') return null;
            return (
              <div key={label} className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white text-right max-w-[60%]">
                  {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                </span>
              </div>
            );
          };

          return (
            <>
            <div className="grid grid-cols-2 gap-5 flex-1 min-h-0 overflow-hidden">
              {/* ── Column 1: Job Details ── */}
              <div className="space-y-4 overflow-y-auto min-h-0 pr-1">
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                    Request Settings
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg px-3 py-1">
                    {detailRow('Source Language', getLanguageName(job?.request?.sourceLanguage) || 'Auto-detect')}
                    {detailRow('Target Language', getLanguageName(job?.request?.targetLanguage))}
                    {detailRow('Speech-to-Text Engine', job?.request?.sttProvider)}
                    {detailRow('Translation Engine', job?.request?.translationProvider)}
                    {detailRow('Text-to-Speech Engine', job?.request?.ttsProvider)}
                    {detailRow('Output Format', job?.request?.outputFormat)}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                    Progress
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{job?.currentStep || '—'}</span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{job?.progress ?? 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                      <div
                        className="bg-blue-500 h-1.5 rounded-full transition-all"
                        style={{ width: `${job?.progress ?? 0}%` }}
                      />
                    </div>
                  </div>
                </div>

                {(job?.status === 'pending' || job?.status === 'processing') && (
                  <div>
                    <button
                      onClick={() => {
                        handleCancelJob(job.jobId);
                        setIsDrawerOpen(false);
                      }}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 text-red-700 dark:text-red-400 text-sm font-medium rounded-lg border border-red-200 dark:border-red-800 transition-colors"
                    >
                      <XMarkIcon className="h-4 w-4" />
                      Cancel Job
                    </button>
                  </div>
                )}

                {job?.status === 'completed' && job?.result && (
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                      Result
                    </h3>
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 space-y-2">
                      {job.result.outputAudioUrl && (
                        <>
                          <audio controls src={job.result.outputAudioUrl} className="w-full h-9" />
                          <button
                            onClick={() => translationApi.downloadFile(job.result!.outputAudioUrl, `audio-translation-${job.jobId}.${job.request?.outputFormat || 'mp3'}`)}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            Download Translated Audio
                          </button>
                          <button
                            onClick={() => navigate(`/app/create-video?translationJobId=${job.jobId}`)}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-green-600 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 text-sm font-medium rounded-lg transition-colors"
                          >
                            <SparklesIcon className="h-4 w-4" />
                            Create Video from this audio
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {job?.error && (
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
                    <p className="text-sm font-medium text-red-700 dark:text-red-400 mb-1">Error</p>
                    <p className="text-sm text-red-600 dark:text-red-300">{job.error}</p>
                  </div>
                )}

                {job?.status === 'failed' && (
                  <div>
                    <button
                      onClick={() => handleRetryJob(job.jobId)}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/20 dark:hover:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400 text-sm font-medium rounded-lg border border-indigo-200 dark:border-indigo-800 transition-colors"
                    >
                      <ArrowPathIcon className="h-4 w-4" />
                      Retry from Last Completed Step
                    </button>
                  </div>
                )}

                {jobDetailsLoading && !job && (
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <ArrowPathIcon className="h-3 w-3 animate-spin" /> Loading details…
                  </div>
                )}
              </div>

              {/* ── Column 2: Steps ── */}
              <div className="flex flex-col min-h-0 overflow-hidden">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2 flex-shrink-0">
                  Processing Steps
                </h3>
                {!job?.steps?.length ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500 italic">No step data available.</p>
                ) : (
                  <div className="space-y-2 overflow-y-auto flex-1">
                    {[...job.steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((step) => (
                      <div key={step.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700/50">
                          {stepStatusIcon(step.status)}
                          <span className="flex-1 text-sm font-medium text-gray-800 dark:text-white truncate">
                            {step.name}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${stepStatusStyle(step.status)}`}>
                            {step.status}
                          </span>
                          {step.duration && (
                            <span className="text-xs text-gray-400 whitespace-nowrap">
                              {formatDuration(step.duration)}
                            </span>
                          )}
                        </div>

                        {(step.data || step.error) && (
                          <div className="px-3 py-2 space-y-1.5 text-sm">
                            {step.error && (
                              <p className="text-red-600 dark:text-red-400 italic">{step.error}</p>
                            )}
                            {step.data?.transcribedText && (
                              <div>
                                <p className="text-sm text-gray-400 mb-0.5">Transcription</p>
                                <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3 bg-gray-50 dark:bg-gray-700/40 rounded p-1.5">
                                  {step.data.transcribedText}
                                </p>
                              </div>
                            )}
                            {step.data?.translatedText && (
                              <div>
                                <p className="text-sm text-gray-400 mb-0.5">Translation</p>
                                <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3 bg-gray-50 dark:bg-gray-700/40 rounded p-1.5">
                                  {step.data.translatedText}
                                </p>
                              </div>
                            )}
                            {step.data?.voiceName && (
                              <p className="text-gray-500 dark:text-gray-400">Voice: <span className="text-gray-700 dark:text-gray-300">{step.data.voiceName}</span></p>
                            )}
                            {step.data?.sourceLanguage && step.data?.targetLanguage && (
                              <p className="text-gray-500 dark:text-gray-400">
                                {step.data.sourceLanguage} → {step.data.targetLanguage}
                              </p>
                            )}
                            {(step.data?.originalAudioUrl || step.data?.outputAudioUrl) && (
                              <audio
                                controls
                                src={step.data.outputAudioUrl || step.data.originalAudioUrl}
                                className="w-full h-8"
                              />
                            )}
                          </div>
                        )}

                        {(step.startedAt || step.completedAt) && (
                          <div className="px-3 pb-1 flex gap-3 text-[10px] text-gray-400">
                            {step.startedAt && <span>Started: {new Date(step.startedAt).toLocaleTimeString()}</span>}
                            {step.completedAt && <span>Done: {new Date(step.completedAt).toLocaleTimeString()}</span>}
                          </div>
                        )}

                        {(job?.status === 'completed' || job?.status === 'failed') && (
                          <div className="px-3 pb-2 pt-1 border-t border-gray-100 dark:border-gray-700">
                            <button
                              onClick={() => setExpandedDrawerStep(step.id)}
                              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded border border-blue-200 dark:border-blue-800 transition-colors"
                            >
                              <MagnifyingGlassPlusIcon className="h-3 w-3" />
                              {step.canRerun ? 'Expand, view & rerun' : 'View step details'}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {expandedDrawerStep && job && (
              <AudioStepDetailModal
                jobId={job.jobId}
                job={job}
                step={job.steps.find((s) => s.id === expandedDrawerStep)!}
                onClose={() => setExpandedDrawerStep(null)}
                onChanged={() => {
                  queryClient.invalidateQueries({ queryKey: ['audio-jobs'] });
                  refreshJobDetails(job.jobId);
                }}
              />
            )}
            </>
          );
        }}
      />
    </>
  );
};

export default AudioTranslation;
